import window from 'ember-window-mock';
// @ts-expect-error -- not typed
import ENV from 'receivable-invoices-js/config/environment';
import { MOLLIE_SCRIPT_ID, MOLLIE_SRC } from 'receivable-invoices-js/constants/mollie';
import { shouldSendToSentry } from 'receivable-invoices-js/react/utils/network';
import { loadScript } from 'receivable-invoices-js/react/utils/load-script';

const MOLLIE_LOCALE = {
  en: 'en_US',
  fr: 'fr_FR',
  de: 'de_DE',
  it: 'it_IT',
  es: 'es_ES',
  pt: 'pt_PT',
  nl: 'nl_NL',
} as const;

interface MollieEvent {
  error?: string;
  touched?: boolean;
  dirty?: boolean;
}

interface MollieComponentInstance {
  mount: (element: HTMLElement | string) => void;
  unmount: () => void;
  addEventListener: (event: string, callback: (event: MollieEvent) => void) => void;
}

interface MollieStyle {
  styles: {
    base: Record<string, string>;
  };
}

interface MollieInstance {
  mount?: (element: HTMLElement | string) => void;
  createComponent?: (id: string, style?: MollieStyle) => MollieComponentInstance;
  createToken?: () => Promise<{ token: string; error: string }>;
}

interface WindowWithMollie {
  Mollie: (profileId: string, options: { testmode: boolean; locale: string }) => MollieInstance;
}

type MollieLocale = keyof typeof MOLLIE_LOCALE;

/**
 * Singleton service that manages the Mollie SDK integration.
 * Handles script loading, initialization, error tracking, and provides
 * an observable interface for components to react to state changes.
 */
class MollieService {
  private _hasError = false;
  private instance: MollieInstance | null = null;
  private loading = false;
  private listeners = new Set<() => void>();

  async load(
    profileId: string,
    locale: string,
    captureException: (error: unknown) => void
  ): Promise<void> {
    if (this.instance || this._hasError || this.loading) {
      return;
    }

    this.loading = true;

    try {
      await loadScript(MOLLIE_SRC, { scriptId: MOLLIE_SCRIPT_ID });

      const mollieLocale =
        locale in MOLLIE_LOCALE ? MOLLIE_LOCALE[locale as MollieLocale] : MOLLIE_LOCALE.en;
      const env = ENV as unknown as { mollie: { testmode: boolean } };
      this.instance = (window as unknown as WindowWithMollie).Mollie(profileId, {
        testmode: env.mollie.testmode,
        locale: mollieLocale,
      });

      this.notifyListeners();
    } catch (error) {
      this._hasError = true;

      if (shouldSendToSentry(error)) {
        captureException(error);
      }

      this.notifyListeners();
    } finally {
      this.loading = false;
    }
  }

  get mount() {
    return this.instance?.mount;
  }

  get createComponent() {
    return this.instance?.createComponent;
  }

  get createToken() {
    return this.instance?.createToken;
  }

  get isLoaded(): boolean {
    return (
      Boolean(document.querySelector(`script#${MOLLIE_SCRIPT_ID}`)) &&
      Boolean(this.instance) &&
      Boolean(this.instance?.createComponent) &&
      Boolean(this.instance?.createToken)
    );
  }

  get hasError(): boolean {
    return this._hasError;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      listener();
    });
  }

  reset(): void {
    this._hasError = false;
    this.instance = null;
    this.loading = false;
    this.listeners.clear();
  }
}

export const mollieService = new MollieService();
